import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, IconButton, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';

interface CandidateRow {
  id: number;
  job_offer_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  position_title: string;
  iq_percent?: number | null;
  stress_percent?: number | null;
}

const CandidatesAllPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<CandidateRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [positions, setPositions] = React.useState<string[]>([]);
  const [positionFilter, setPositionFilter] = React.useState<string>('');

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/recruit/candidates');
        const list: CandidateRow[] = data?.data || [];
        if (!mounted) return;
        setRows(list);
        const uniq = Array.from(new Set(list.map(r => r.position_title).filter(Boolean)));
        setPositions(uniq);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = rows.filter(r => !positionFilter || r.position_title === positionFilter);

  return (
    <Box sx={{ p: 2 }}>
      <Button onClick={()=> navigate(-1)} sx={{ mb: 2 }}>Powrót</Button>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Kandydaci</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="pos-filter-label">Stanowisko</InputLabel>
          <Select labelId="pos-filter-label" label="Stanowisko" value={positionFilter} onChange={(e)=>setPositionFilter(e.target.value)} displayEmpty>
            <MenuItem value="">Wszystkie</MenuItem>
            {positions.map(p => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}>ID</TableCell>
              <TableCell>Imię i nazwisko</TableCell>
              <TableCell>Adres e‑mail</TableCell>
              <TableCell sx={{ width: 160 }}>Telefon</TableCell>
              <TableCell>Stanowisko</TableCell>
              <TableCell sx={{ width: 120 }}>Test IQ</TableCell>
              <TableCell sx={{ width: 120 }}>Stress Test</TableCell>
              <TableCell sx={{ width: 60 }} align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.id}</TableCell>
                <TableCell>{r.full_name}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell>{r.phone || '—'}</TableCell>
                <TableCell>{r.position_title}</TableCell>
                <TableCell>{r.iq_percent != null ? `${r.iq_percent}%` : '—'}</TableCell>
                <TableCell>{r.stress_percent != null ? `${r.stress_percent}%` : '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={()=> navigate(`/hr/offers/${r.job_offer_id}/candidates`)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && !loading && (
              <TableRow><TableCell colSpan={8}>Brak kandydatów.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CandidatesAllPage;
